import { alpha, darken, lighten, type Theme } from '@mui/material/styles';
import {
  severityColors,
  statusColors,
  iconColors,
  textColors,
  brand,
  inputColors,
  lightInputColors,
} from '@/tokens/colors';
import { dsVar } from '@/tokens/surfaceScopes';
import { controlHeights, chipHeights } from '@/tokens/sizes';
import { spacing } from '@/tokens/spacing';
import { fontSize, lineHeight } from '@/tokens/typography';

/**
 * Custom component variants layered on top of MUI's defaults.
 *
 * These are referenced from `components.ts` and surfaced to consumers through
 * theme augmentation, so `<Button variant="primary" />` is fully typed.
 *
 * The chip variants below are the styling layer that `Chip`'s `purpose` prop
 * selects from -- they are no longer something a caller picks directly.
 */

/* ------------------------------------------------------------------ */
/* Chip color map                                                      */
/* ------------------------------------------------------------------ */

/**
 * The canonical chip color palette. Two orthogonal axes:
 *   variant (filled | subtle | outlined | dot) x color (10 keys below).
 *
 * This cross-product is the palette `Chip` derives from, not a menu for
 * callers: `purpose` picks the legitimate pairs. See `src/atoms/Chip/Chip.tsx`.
 *
 * MUI already handles filled/outlined for palette-native colors (primary,
 * info, success, warning, error). We generate custom theme variants for
 * the five non-palette colors (neutral, critical, high, medium, low) on
 * filled/outlined, and for ALL colors on the two new custom variants
 * (subtle, dot).
 */
export const chipColorMap = {
  neutral: textColors.secondary,
  info: statusColors.info,
  success: statusColors.success,
  warning: statusColors.warning,
  error: statusColors.failure,
  critical: severityColors.critical,
  high: iconColors.vulnerability,
  medium: severityColors.medium,
  low: severityColors.low,
  primary: brand.main,
} as const;

export type ChipColorKey = keyof typeof chipColorMap;
const chipColorKeys = Object.keys(chipColorMap) as ChipColorKey[];

/**
 * Colors that MUI's palette already supports for filled/outlined --
 * we skip generating custom theme variants for these on those two variants.
 */
const muiPaletteColors = new Set<string>([
  'primary',
  'info',
  'success',
  'warning',
  'error',
]);

const customColorKeys = chipColorKeys.filter((k) => !muiPaletteColors.has(k));

/* ------------------------------------------------------------------ */
/* Luminance helper                                                    */
/* ------------------------------------------------------------------ */

/**
 * Relative luminance (WCAG) of a hex color. Used to pick white or dark
 * label text so the chip meets contrast requirements.
 */
function relativeLuminance(hex: string): number {
  const value = hex.replace('#', '');
  const channels = [0, 2, 4].map((i) => {
    const c = parseInt(value.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

const LIGHT_TEXT_LUMINANCE_THRESHOLD = 0.3;

/** WCAG contrast of a hex colour against white. Chip labels sit on paper. */
function contrastAgainstWhite(hex: string): number {
  return 1.05 / (relativeLuminance(hex) + 0.05);
}

/**
 * Channel-multiply darken that stays in 6-digit hex, so the result can go
 * back through `relativeLuminance`. MUI's `darken` returns `rgb(...)`, which
 * our luminance helper rejects.
 */
function darkenHex(hex: string, amount: number): string {
  const value = hex.replace('#', '');
  const channels = [0, 2, 4].map((i) =>
    Math.round(parseInt(value.slice(i, i + 2), 16) * (1 - amount)),
  );
  return `#${channels.map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Text color for the subtle variant on the *dark* surface. Dark colors (low
 * luminance) are lightened so they read clearly. Unlike a filled chip this
 * never falls back to white — the point of subtle is that the text carries
 * the hue.
 */
function subtleTextColor(hex: string): string {
  const lum = relativeLuminance(hex);
  if (lum < 0.08) return lighten(hex, 0.45);
  if (lum < 0.15) return lighten(hex, 0.25);
  return hex;
}

/**
 * Label (and matching border) ink for outlined and subtle chips.
 *
 * Dark theme keeps the historical lightening of very dark hues so they read
 * on `#141414`. Light theme must never lighten: that correction drops reds
 * and yellows below AA on white (Low was 1.76:1, a lightened Critical 3.03:1).
 * Pale hues are darkened until the 12px label clears 4.5:1 against white.
 *
 * Neutral is overridden separately via `theme.palette.text.secondary` so it
 * tracks the body secondary ink in both modes rather than darkening the
 * dark-theme `#AEAEAE` token into an ad-hoc grey.
 */
function chipLabelInk(hex: string, mode: 'light' | 'dark'): string {
  if (mode === 'dark') return subtleTextColor(hex);
  if (contrastAgainstWhite(hex) >= 4.5) return hex;
  for (let amount = 0.05; amount <= 0.65; amount += 0.05) {
    const candidate = darkenHex(hex, amount);
    if (contrastAgainstWhite(candidate) >= 4.5) return candidate;
  }
  return darkenHex(hex, 0.65);
}

/* ------------------------------------------------------------------ */
/* Chip variant: subtle                                                */
/* ------------------------------------------------------------------ */

/**
 * Tinted background (~12% opacity), colored text, subtle border (~25%
 * opacity). Equivalent to Astryx Badge `appearance="subtle"`.
 *
 * The tint is what lifts a chip off the surface, so this is for pills that
 * should catch the eye in their own colour: severities, statuses, verdicts, and
 * the unselected half of `purpose="select"`, where a toggleable option needs to
 * look unlike the static tags around it. The neutral pills that are meant to be
 * read past -- `category`, `metric`, `filter` -- use `fieldChipVariants`.
 *
 * Ink is mode-aware: see `chipLabelInk`. The tint keeps the original hue so a
 * darkened light-mode label still sits in a recognisably coloured pill.
 */
export const subtleChipVariants = chipColorKeys.map((key) => {
  const hex = chipColorMap[key];
  const bgOpacity = relativeLuminance(hex) < 0.08 ? 0.18 : 0.12;
  return {
    props: { variant: 'subtle' as const, color: key },
    style: ({ theme }: { theme: Theme }) => {
      const text = chipLabelInk(hex, theme.palette.mode);
      return {
        backgroundColor: alpha(hex, bgOpacity),
        color: text,
        border: `1px solid ${alpha(hex, 0.25)}`,
        '&:hover': { backgroundColor: alpha(hex, bgOpacity + 0.06) },
        '& .MuiChip-deleteIcon': { color: alpha(text, 0.6), '&:hover': { color: text } },
      };
    },
  };
});

/**
 * Subtle + neutral.
 *
 * `chipColorMap.neutral` is the dark-theme `#AEAEAE`, and on white that is
 * 2.22:1 — the washed-out Cloud / unknown-entity look. Deriving from
 * `theme.palette.text.secondary` gives the same ink field chips and body
 * secondary already use (7.73:1 on white; `#AEAEAE` unchanged in dark).
 *
 * Registered after `subtleChipVariants` so this pair wins.
 */
export const neutralSubtleChipVariants = [
  {
    props: { variant: 'subtle' as const, color: 'neutral' as const },
    style: ({ theme }: { theme: Theme }) => {
      const ink = theme.palette.text.secondary;
      const fill =
        theme.palette.mode === 'light' ? theme.palette.text.primary : ink;
      const bgOpacity = theme.palette.mode === 'light' ? 0.06 : 0.12;
      return {
        backgroundColor: alpha(fill, bgOpacity),
        color: ink,
        border: `1px solid ${alpha(ink, 0.25)}`,
        '&:hover': { backgroundColor: alpha(fill, bgOpacity + 0.06) },
        '& .MuiChip-deleteIcon': { color: alpha(ink, 0.6), '&:hover': { color: ink } },
      };
    },
  },
];

/* ------------------------------------------------------------------ */
/* Chip variant: field                                                 */
/* ------------------------------------------------------------------ */

/**
 * The chrome of an outlined input, worn by a chip: an `inputColors.background`
 * fill inside an `inputColors.border` outline, both deliberately quiet.
 *
 * This is what `category`, `metric`, `filter`, `picker` and `overflow` render
 * as -- every neutral pill, static or operable. The point is that a filter bar
 * is a row of form controls, so the pills in it should match the TextField and
 * the secondary Button beside them rather than announcing themselves as their
 * own kind of object. Sharing `inputColors` with `MuiOutlinedInput` means they
 * match by token, not by a coincidental hex that a later restyle would break.
 *
 * Interactivity is carried by affordances rather than by the border: a picker
 * has a chevron, an overflow chip reads `+N more`, a filter chip has an X. An
 * earlier draft gave the operable ones a brighter outline, which made a filter
 * chip and the picker beside it look like different species of pill when all
 * that differs is what you can do to them.
 *
 * `subtleChipVariants` tints with translucent white, which lifts a chip *off*
 * the surface -- correct for a status or severity pill that should catch the
 * eye, wrong for a tag the user is meant to read past.
 *
 * Both modes share `--ds-field` / `--ds-field-border` with OutlinedInput and
 * the secondary Button. Light falls back to `lightInputColors`, not the dark
 * `inputColors` hexes — those painted near-black pills on a light canvas.
 *
 * Note the trade-off this buys. The fill is close enough to the host that on
 * some rungs the border does nearly all the work of drawing the pill. The label
 * carries the AA obligation, so what stays quiet is the boundary, not the
 * content -- but that means these chips identify themselves by their text and
 * their X rather than by a crisp edge. Do not reach for this variant where the
 * outline is the only thing distinguishing two states.
 */
export const fieldChipVariants = [
  {
    props: { variant: 'field' as const },
    style: ({ theme }: { theme: Theme }) => {
      // Tracks the surface scope for the same reason as the secondary Button:
      // these pills are supposed to sit at the level of the form controls beside
      // them, and that is only true on every rung if the fill recesses with the
      // field rather than staying at one fixed token.
      const dark = theme.palette.mode === 'dark';
      const chrome = dark ? inputColors : lightInputColors;
      const fill = dsVar('field', chrome.background);
      const border = dsVar('fieldBorder', chrome.border);
      const ink = theme.palette.text.secondary;
      return {
        backgroundColor: fill,
        color: ink,
        border: `1px solid ${border}`,
        // Scoped to the interactive cases. `subtle` lightens on any hover, so a
        // static category tag shimmers under the pointer as though it were a
        // target; a tag that cannot be clicked should not answer the mouse.
        '&.MuiChip-clickable:hover, &.MuiChip-deletable:hover': {
          backgroundImage: `linear-gradient(${dsVar('hover', dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)')}, ${dsVar('hover', dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)')})`,
        },
        '& .MuiChip-deleteIcon': {
          color: alpha(ink, 0.6),
          '&:hover': { color: theme.palette.text.primary },
        },
      };
    },
  },
];

/* ------------------------------------------------------------------ */
/* Chip variant: dot                                                   */
/* ------------------------------------------------------------------ */

/**
 * No background or border. A leading colored dot + neutral label text.
 * Pure status indicator. The dot is rendered via a `::before` pseudo-element
 * on the chip root so callers don't need to pass an icon prop.
 * Equivalent to Astryx Badge `appearance="dot"`.
 *
 * The label uses `theme.palette.text.secondary`, not the flat dark-theme
 * `textColors.secondary` token: on white that token is 2.22:1, which is what
 * made the whole status row in the purpose ladder unreadable in light mode.
 */
export const dotChipVariants = chipColorKeys.map((key) => {
  const hex = chipColorMap[key];
  return {
    props: { variant: 'dot' as const, color: key },
    style: ({ theme }: { theme: Theme }) => ({
      backgroundColor: 'transparent',
      color: theme.palette.text.secondary,
      border: 'none',
      paddingLeft: '4px',
      '&::before': {
        content: '""',
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: hex,
        marginRight: 6,
        marginLeft: 4,
        flexShrink: 0,
      },
      // The dot's own margin is not the whole gap: MUI pads the label by 12px
      // (8px when small) to hold text off a pill's edge. This variant has no
      // pill -- no background, no border -- so that padding was stacking on the
      // margin and reading as a 18px gulf between the dot and its word. Zeroing
      // the leading side leaves the 6px above as the actual gap.
      '& .MuiChip-label': { paddingLeft: 0 },
      '&:hover': { backgroundColor: 'transparent' },
    }),
  };
});

/* ------------------------------------------------------------------ */
/* Chip colors: filled + outlined for non-palette colors               */
/* ------------------------------------------------------------------ */

/**
 * Filled chip styles for colors that MUI's palette doesn't cover
 * (neutral, critical, high, medium, low). Solid background, high-contrast
 * text — use sparingly for the most prominent states.
 */
export const filledChipColorVariants = customColorKeys.map((key) => {
  const hex = chipColorMap[key];
  const bg = relativeLuminance(hex) < 0.08 ? lighten(hex, 0.35) : hex;
  const bgLum = relativeLuminance(bg);
  const isDark = bgLum < LIGHT_TEXT_LUMINANCE_THRESHOLD;
  const text = isDark ? '#FFFFFF' : '#1A1A1A';
  return {
    props: { variant: 'filled' as const, color: key },
    style: {
      backgroundColor: bg,
      color: text,
      '&:hover': { backgroundColor: lighten(bg, 0.1) },
      '& .MuiChip-deleteIcon': {
        color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.5)',
        '&:hover': { color: text },
      },
    },
  };
});

/**
 * Outlined chip styles for non-palette colors. Transparent background,
 * 1px color border, colored text. Ink is mode-aware via `chipLabelInk` so
 * Low / Medium yellows clear AA on white instead of shipping the dark-theme
 * tokens flat (1.76:1 / 2.27:1), and so Critical is not lightened into a
 * pink that fails on the light surface.
 */
export const outlinedChipColorVariants = customColorKeys.map((key) => {
  const hex = chipColorMap[key];
  return {
    props: { variant: 'outlined' as const, color: key },
    style: ({ theme }: { theme: Theme }) => {
      const text = chipLabelInk(hex, theme.palette.mode);
      return {
        backgroundColor: 'transparent',
        color: text,
        border: `1px solid ${text}`,
        '&:hover': { backgroundColor: alpha(hex, 0.08) },
        '& .MuiChip-deleteIcon': { color: alpha(text, 0.6), '&:hover': { color: text } },
      };
    },
  };
});

/**
 * Outlined + neutral.
 *
 * No `purpose` renders as this any more -- `picker` and `overflow` moved to
 * `fieldChipVariants` so the whole filter family shares one chrome. It stays for
 * the legacy `variant`/`color` API, which consumers can still pass directly, and
 * because the contrast fix below is the reason it exists at all.
 *
 * `chipColorMap.neutral` is `textColors.secondary` (`#AEAEAE`), a dark-theme
 * token, and `outlinedChipColorVariants` applies it as a flat value in both
 * modes. On the light theme's white paper that is **2.22:1** for the label and
 * the same for the border -- under the 4.5:1 AA floor for text and under the
 * 3:1 floor for a non-text boundary. The overflow chip has shipped with this;
 * the picker chip is the one whose entire label is drawn in it, which is what
 * made it worth fixing rather than recording.
 *
 * Deriving from `theme.palette.text.secondary` gives 7.73:1 on white, and in
 * dark mode that palette entry *is* `#AEAEAE`, so the dark theme is unchanged
 * to the pixel. Same technique as `lifecycleChipVariants` below, and the same
 * reason: a flat token cannot be correct in two modes.
 *
 * Registered after `outlinedChipColorVariants` in `components.ts`, since both
 * match this pair and the later variant wins.
 */
export const neutralOutlinedChipVariants = [
  {
    props: { variant: 'outlined' as const, color: 'neutral' as const },
    style: ({ theme }: { theme: Theme }) => {
      const ink = theme.palette.text.secondary;
      return {
        backgroundColor: 'transparent',
        color: ink,
        border: `1px solid ${ink}`,
        '&:hover': { backgroundColor: alpha(ink, 0.08) },
        '& .MuiChip-deleteIcon': { color: alpha(ink, 0.6), '&:hover': { color: ink } },
      };
    },
  },
];

/**
 * Outlined + primary, the appearance behind `Chip purpose="lifecycle"`.
 *
 * MUI already styles this pair, but its label takes `primary.main` unmodified,
 * and in the light theme that teal on white is 4.32:1 -- under the 4.5:1 AA
 * floor for text this size. Darkening it 10% there brings it to 5.17:1 while
 * the dark theme keeps the brighter token at ~8:1. Deriving from the palette
 * rather than the `brand` token is what makes that per-mode difference
 * possible at all.
 *
 * No hover style: every lifecycle stage is static, and a hover response on a
 * chip that cannot be clicked is a promise the component does not keep.
 */
export const lifecycleChipVariants = [
  {
    props: { variant: 'outlined' as const, color: 'primary' as const },
    style: ({ theme }: { theme: Theme }) => {
      const ink =
        theme.palette.mode === 'light'
          ? darken(theme.palette.primary.main, 0.1)
          : theme.palette.primary.main;
      return {
        backgroundColor: 'transparent',
        color: ink,
        border: `1px solid ${ink}`,
      };
    },
  },
];

/**
 * Button variants - the design system's canonical emphasis ladder:
 *
 * - `primary` - the single most important action in a view (solid brand accent).
 * - `secondary` - the default; a bordered, neutral, low-chrome action. Reads as
 *   a sibling of a text field or select at the same height, so it is the right
 *   choice for toolbars and inline groups of actions.
 * - `ghost` - lowest emphasis; transparent until hovered.
 * - `destructive` - irreversible/dangerous actions (delete, isolate, simulate);
 *   always pair with a confirmation step.
 *
 * These are custom variants, so each fully specifies its own background, text,
 * border, hover and disabled styling (MUI's built-in variant styles do not
 * apply). `borderRadius`/`textTransform`/`fontWeight` come from the shared
 * `MuiButton.root` override in `components.ts`.
 */
export const buttonVariants = [
  {
    props: { variant: 'primary' as const },
    style: ({ theme }: { theme: Theme }) => ({
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      border: '1px solid transparent',
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
      '&.Mui-disabled': {
        backgroundColor: theme.palette.action.disabledBackground,
        color: theme.palette.action.disabled,
      },
    }),
  },
  {
    props: { variant: 'secondary' as const },
    style: ({ theme }: { theme: Theme }) => {
      // Neutral, not brand-tinted. This is the default variant, so it sits in
      // toolbars beside text fields and selects far more often than it sits
      // beside a primary action -- a turquoise-on-green button next to a
      // neutral outlined input read as the loudest thing in the row, and it
      // also spent the brand accent on the most ordinary control in the system.
      //
      // Shares `--ds-field` / `--ds-field-border` with OutlinedInput so a
      // secondary Button and a TextField of the same height match by token in
      // both modes. Light falls back to `lightInputColors`; the dark
      // `inputColors` hexes must never paint a light canvas.
      //
      // Hover cannot use `lighten()` any more, because the fill is a `var()`
      // and `lighten()` needs a resolved colour. Layering `--ds-hover` as a
      // gradient over the background achieves the same one-step lift, works on
      // whatever the variable resolved to, and — unlike `filter` or `opacity` —
      // does not touch the label.
      const dark = theme.palette.mode === 'dark';
      const chrome = dark ? inputColors : lightInputColors;
      // Same `--ds-field` / `--ds-field-border` as OutlinedInput, so a secondary
      // Button and a TextField of the same height match by token in both modes.
      const fill = dsVar('field', chrome.background);
      const border = dsVar('fieldBorder', chrome.border);
      return {
        backgroundColor: fill,
        color: theme.palette.text.primary,
        border: `1px solid ${border}`,
        '&:hover': {
          ...(dark
            ? {
                backgroundImage: `linear-gradient(${dsVar('hover', 'rgba(255,255,255,0.06)')}, ${dsVar('hover', 'rgba(255,255,255,0.06)')})`,
              }
            : {
                backgroundImage: `linear-gradient(${dsVar('hover', 'rgba(0,0,0,0.04)')}, ${dsVar('hover', 'rgba(0,0,0,0.04)')})`,
              }),
          borderColor: chrome.borderHover,
        },
        '&.Mui-disabled': {
          backgroundColor: theme.palette.action.disabledBackground,
          color: theme.palette.action.disabled,
          borderColor: theme.palette.action.disabledBackground,
        },
      };
    },
  },
  {
    props: { variant: 'ghost' as const },
    style: ({ theme }: { theme: Theme }) => ({
      backgroundColor: 'transparent',
      color: theme.palette.text.primary,
      border: '1px solid transparent',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
      '&.Mui-disabled': {
        backgroundColor: 'transparent',
        color: theme.palette.action.disabled,
      },
    }),
  },
  {
    props: { variant: 'destructive' as const },
    style: ({ theme }: { theme: Theme }) => ({
      backgroundColor: severityColors.high,
      color: '#FFFFFF',
      border: '1px solid transparent',
      '&:hover': {
        backgroundColor: severityColors.critical,
      },
      '&.Mui-disabled': {
        backgroundColor: theme.palette.action.disabledBackground,
        color: theme.palette.action.disabled,
      },
    }),
  },
];

/**
 * Button size scale.
 *
 * `minHeight` is the lever that fixes the control height: the padding below is
 * deliberately smaller than the height implies, so the box lands on exactly
 * `controlHeights` (28/36/44) for single-line labels but still breathes if a
 * label wraps. This also keeps the sizes correct whether or not
 * `box-sizing: border-box` is in effect, since the 1px border every variant
 * carries is never subtracted from a padding value.
 *
 * The same `controlHeights` tokens size OutlinedInput / TextField / Select, so
 * a Button and a field of the same `size` match by token rather than by tuning
 * two independent padding recipes until they happen to agree.
 */
const buttonSizes = {
  small: {
    minHeight: controlHeights.small,
    fontSize: fontSize.sm,
    padding: `${spacing.xs}px ${spacing.sm + spacing.xs}px`,
  },
  medium: {
    minHeight: controlHeights.medium,
    fontSize: fontSize.md,
    padding: `${spacing.xs}px ${spacing.md}px`,
  },
  large: {
    minHeight: controlHeights.large,
    fontSize: fontSize.lg,
    padding: `${spacing.sm}px ${spacing.lg}px`,
  },
} as const;

const buttonSizeNames = Object.keys(buttonSizes) as (keyof typeof buttonSizes)[];

/**
 * Size styles for the custom button variants.
 *
 * MUI ships its own size padding, but every one of those rules is matched
 * against `variant: 'text' | 'outlined' | 'contained'`. The emphasis ladder
 * above replaces those variants entirely, so without this cross-product a
 * `small` and a `large` Button render at identical dimensions. Scoping to the
 * custom variants leaves MUI's built-in variants on their stock sizing.
 */
export const buttonSizeVariants = buttonVariants.flatMap(({ props: { variant } }) =>
  buttonSizeNames.map((size) => ({
    props: { variant, size },
    style: { ...buttonSizes[size], lineHeight: lineHeight.normal },
  })),
);

/**
 * Chip rungs that MUI does not ship (`large` / `extraLarge`).
 *
 * `small` and `medium` stay on `MuiChip` styleOverrides so existing
 * call sites do not move. These variants are keyed only on `size`.
 */
export const chipSizeVariants = [
  {
    props: { size: 'large' as const },
    style: {
      height: chipHeights.large,
      fontSize: fontSize.md,
    },
  },
  {
    props: { size: 'extraLarge' as const },
    style: {
      height: chipHeights.extraLarge,
      fontSize: fontSize.lg,
    },
  },
];
